import { Box, Center } from '@chakra-ui/react';
import { DataList } from '@gamma/ui/data-table';
import { LoadingSpinner } from '@gamma/ui/progress';
import { useContext } from 'react';
import { SortingRule } from 'react-table';
import { i18n } from '../../../../shared/localization';
import { SantizedDetection } from '../../pages';
import { Detection } from '@investigator/shared/generated/graphql';
import { DetectionDrawer, DetectionDrawerActions } from '../DetectionDrawer';
import { DetectionRow } from '../DetectionRow';
import { SlideDrawerContext } from '../../../../shared/providers/SlideDrawerProvider';
import { useDetectionContext } from '../../context';

const { controls } = i18n.pages.detections;

interface DetectionsListViewProps {
  offset: number;
  pageSize: number;
  pageIndex: number;
  pageCount: number;
  isRelated?: boolean;
  params: URLSearchParams;
  detectionsLoading: boolean;
  formatSort: (value: string) => { id: string; desc: boolean }[];
  handleFetchData: (
    pageIndex: number,
    pageSize: number,
    sortBy?: SortingRule<SantizedDetection>[],
  ) => void;
  getSortsForAPI: (
    sortBy: SortingRule<Detection>[],
  ) => { sort_by: string; sort_dir: string }[];
}

export const DetectionsListView = ({
  params,
  isRelated,
  offset,
  pageSize,
  pageIndex,
  pageCount,
  formatSort,
  getSortsForAPI,
  handleFetchData,
  detectionsLoading,
}: DetectionsListViewProps) => {
  const {
    setIsSlideDrawerOpen,
    setPortalType,
    setSlideDrawerTitle,
    setSlideDrawerBody,
    setSlideDrawerActions,
  } = useContext(SlideDrawerContext);

  const {
    selectedItem,
    setSelectedItem,
    queryVariables,
    detectionData,
    detectionsData,
  } = useDetectionContext();

  const listData = detectionsData?.queryDetectionsPaginated?.detections || [];
  if (params.get('pg') && pageIndex > 0 && listData.length === 0)
    return <LoadingSpinner />;

  return (
    <Box
      borderTop="1px solid"
      borderColor="border.layer.2"
      data-testid="data-list-wrapper"
    >
      <DataList
        storageKey="investigator-pages-detections-list"
        isPaginationManual
        splitWidth="493px"
        isLoading={detectionsLoading}
        pageCount={pageCount}
        RenderItem={DetectionRow}
        selectedItem={selectedItem}
        onFetchData={handleFetchData}
        onItemSelection={(item) => {
          if (selectedItem && selectedItem.detection_id !== item.detection_id) {
            setIsSlideDrawerOpen?.(false);
            setSelectedItem(item);
            setSlideDrawerTitle?.(
              <Box
                key={`${item.detection_id}-detection-portal-title`}
                __css={{ wordWrap: 'anywhere' }}
              >
                {`${item?.alert_info?.alert_name} | ${item?.alert_entity?.entity_name}`}
              </Box>,
            );
            setSlideDrawerBody?.(
              <DetectionDrawer
                key={`${item.detection_id}-detection-portal-body`}
                item={item}
                disableBorders={true}
                queryDetectionsPaginatedVariables={{
                  query: queryVariables?.query ?? '',
                  size: queryVariables?.size ?? 0,
                  offset: queryVariables?.offset ?? 0,
                  sort: getSortsForAPI(
                    formatSort(queryVariables?.sortBy ?? ''),
                  ),
                }}
              />,
            );
            setSlideDrawerActions?.(
              <Box w="100%" my={2}>
                <DetectionDrawerActions
                  key={`${item.detection_id}-detection-portal-actions`}
                  detection={detectionData as Detection}
                  queryDetectionsPaginatedVariables={{
                    query: queryVariables?.query ?? '',
                    size: queryVariables?.size ?? 0,
                    offset: queryVariables?.offset ?? 0,
                    sort: getSortsForAPI(
                      formatSort(queryVariables?.sortBy ?? ''),
                    ),
                  }}
                />
              </Box>,
            );
            setPortalType?.('detection');
          }
        }}
        initialState={{
          pageSize,
          pageIndex,
        }}
        emptyBodyContent={
          listData?.length === 0 && <Center m={4}>{controls.noResults}</Center>
        }
        autoResetPage={offset === 0 || offset / pageSize >= pageCount}
        itemCount={detectionsData?.queryDetectionsPaginated?.total_items}
        data={listData}
        matchFn={(item, selected) =>
          item.detection_id === selected?.detection_id
        }
        RenderSplit={
          selectedItem
            ? (item) => (
                <DetectionDrawer
                  {...item}
                  isRelated={isRelated}
                  queryDetectionsPaginatedVariables={{
                    query: queryVariables?.query ?? '',
                    size: queryVariables?.size ?? 0,
                    offset: queryVariables?.offset ?? 0,
                    sort: getSortsForAPI(
                      formatSort(queryVariables?.sortBy ?? ''),
                    ),
                  }}
                />
              )
            : undefined
        }
        actions={
          queryVariables &&
          !detectionsLoading &&
          selectedItem && (
            <DetectionDrawerActions
              detection={detectionData as Detection}
              queryDetectionsPaginatedVariables={{
                query: queryVariables.query,
                size: queryVariables.size,
                offset: queryVariables.offset,
                sort: getSortsForAPI(formatSort(queryVariables?.sortBy ?? '')),
              }}
            />
          )
        }
      />
    </Box>
  );
};
